import React, {useState, useEffect, useCallback, useMemo, useRef} from "react";
import {useTranslation} from "react-i18next";
import PageHeader from "./components/PageHeader";
import DateRangePicker from "./components/DateRangePicker";
import TimeRangePicker from "./components/TimeRangePicker";
import StatsCards from "./components/StatsCards";
import ChartSection from "./components/ChartSection";
import ExpandableTable from "./components/ExpandableTable";
import TableMetrics from "./components/TableMetrics";
import OrderTypeMetrics from "./components/OrderTypeMetrics";
import ExportPopup from "./components/ExportPopup";
import ExportConfirmPopup from "./components/ExportConfirmPopup";
import CompareAccountsModal from "./components/CompareAccountsModal";
import ComparePeriodSelector from "./components/ComparePeriodSelector";
import {COMPARE_MODES, getComparisonRange} from "./comparison";
import JournauxTables from "./JournauxTables";
import Ventes from "./Ventes";
import AnalyticsDashboard from "./dashboard/AnalyticsDashboard";
import ProductFilterPicker from "./productFilter/ProductFilterPicker";
import ProductStatsView from "./productFilter/ProductStatsView";
import {readStoredSelection, storeSelection} from "./productFilter/productStatsApi";
import "./ActivityPage.scss";
import Employees from "./Employees";
import usePersistedTab from "../../hooks/usePersistedTab";
import secureStorage from "../../utils/secureStorage";
import {toLocalDateStr} from "../../utils/dateHelpers";
import {useDarkMode} from "../../contexts/DarkModeContext";

const ActivityPage = ({isDarkMode: isDarkModeProp}) => {
    const {t} = useTranslation("activite");
    const {isDarkMode: contextDarkMode} = useDarkMode();
    const isDarkMode = typeof isDarkModeProp === 'boolean' ? isDarkModeProp : contextDarkMode;

    // Date range state (with localStorage persistence)
    const [dateRange, setDateRange] = useState(() => {
        try {
            const stored = localStorage.getItem("activity_dateRange");
            if (stored) {
                const parsed = JSON.parse(stored);
                // Convert string dates back to Date objects
                return [{
                    // Use YYYY-MM-DD string parsing (local midnight) to avoid
                    // the UTC shift that `new Date(isoString)` introduces.
                    startDate: new Date(`${toLocalDateStr(new Date(parsed[0].startDate))}T00:00:00`),
                    endDate:   new Date(`${toLocalDateStr(new Date(parsed[0].endDate))}T00:00:00`),
                    key: "selection",
                }];
            }
        } catch (err) {
            console.error("Error loading date range from localStorage:", err);
        }
        return [{
            startDate: new Date(),
            endDate: new Date(),
            key: "selection",
        }];
    });

    // Time state — defaults to the full day, but remains user-editable.
    const [startTime, setStartTime] = useState("00:00");
    const [endTime, setEndTime] = useState("23:59");
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    // Stores a stable period key (e.g. "today", "thisWeek") — or the "quick"
    // sentinel for the default state — never a pre-translated label, so the
    // header can re-resolve the display text whenever the language changes.
    const [selectedPeriodLabel, setSelectedPeriodLabel] = useState(() => {
        try {
            const stored = localStorage.getItem("activity_periodLabel");
            return stored || "quick";
        } catch {
            return "quick";
        }
    });

    // Period comparison ("none" | "previousPeriod" | "previousYear"), persisted
    // like the date range so it survives a reload.
    const [compareMode, setCompareMode] = useState(() => {
        try {
            const stored = localStorage.getItem("activity_compareMode");
            return COMPARE_MODES.includes(stored) ? stored : "none";
        } catch {
            return "none";
        }
    });

    useEffect(() => {
        try {
            localStorage.setItem("activity_compareMode", compareMode);
        } catch (err) {
            console.error("Error saving compare mode to localStorage:", err);
        }
    }, [compareMode]);

    // Filtre « Produits / Catégories » du Résumé : [{type, id, name}], gardé
    // par navigateur comme la période. Actif → le Résumé montre les stats de
    // la sélection à la place des stats globales.
    const [productFilter, setProductFilter] = useState(readStoredSelection);
    useEffect(() => storeSelection(productFilter), [productFilter]);

    // Memoized so children's fetch effects only re-run when it really changes.
    const comparisonRange = useMemo(
        () => getComparisonRange(dateRange, compareMode),
        [dateRange, compareMode]
    );

    // Save dateRange to localStorage whenever it changes
    useEffect(() => {
        try {
            localStorage.setItem("activity_dateRange", JSON.stringify(dateRange));
        } catch (err) {
            console.error("Error saving date range to localStorage:", err);
        }
    }, [dateRange]);

    // Save selectedPeriodLabel to localStorage whenever it changes
    useEffect(() => {
        try {
            localStorage.setItem("activity_periodLabel", selectedPeriodLabel);
        } catch (err) {
            console.error("Error saving period label to localStorage:", err);
        }
    }, [selectedPeriodLabel]);

    // Tab navigation states
    const tabs = [
        {key: "resumer", label: t("activityPage.tabs.resumer")},
        {key: "journaux", label: t("activityPage.tabs.journaux")},
        {key: "ventes", label: t("activityPage.tabs.ventes")},
        {key: "employee", label: t("activityPage.tabs.employee")},
    ];

    const [active, setActive] = usePersistedTab("tab_activity_main", "resumer");
    const [activeTab1, setActiveTab1] = usePersistedTab("tab_activity_table1", "payments");
    const [activeTab2, setActiveTab2] = usePersistedTab("tab_activity_table2", "produits");
    const [activeRestaurantTab, setActiveRestaurantTab] = usePersistedTab("tab_activity_restaurant", "tables");

    // Show table metrics only for restaurant accounts (busniessType === "004")
    const [isRestaurant, setIsRestaurant] = useState(false);
    // Appointment KPIs only for beauty salons (busniessType === "001")
    const [isBeautySalon, setIsBeautySalon] = useState(false);
    useEffect(() => {
        secureStorage.getItem("selectedAccount").then(acc => {
            setIsRestaurant(acc?.busniessType === "004");
            setIsBeautySalon(acc?.busniessType === "001");
        });
    }, []);

    // ── Refresh ────────────────────────────────────────────────────────────────
    const [refreshKey, setRefreshKey] = useState(0);
    const [isRefreshing, setIsRefreshing] = useState(false);

    const handleRefresh = useCallback(() => {
        setIsRefreshing(true);
        setRefreshKey(k => k + 1);
        // Reset spinner after 1.5 s (components' own loaders take over)
        setTimeout(() => setIsRefreshing(false), 1500);
    }, []);
    // Table tabs options
    const tabOptions1 = [
        {key: "paiements", label: t("activityPage.table1.paiements")},
        {key: "taxes", label: t("activityPage.table1.taxes")},
        {key: "mouvements", label: t("activityPage.table1.mouvements")},
    ];

    // Table tabs options
    const tabOptions2 = [
        {key: "produits", label: t("activityPage.table2.produits")},
        {key: "categories", label: t("activityPage.table2.categories")},
        {key: "remises", label: t("activityPage.table2.remises")},
        {key: "menus", label: isRestaurant ? t("activityPage.table2.menus") : t("activityPage.table2.packs")},
        {key: "channels", label: t("activityPage.table2.channels")},
        ...(isBeautySalon ? [{key: "rendezvous", label: t("activityPage.table2.rendezvous")}] : []),
    ];
    // The tab is remembered per browser: another account may not have it
    const visibleTab2 = tabOptions2.some(tab => tab.key === activeTab2) ? activeTab2 : "produits";

    // Expanded table: null, "table1" (payments/taxes/...) or "table2" (products/...).
    // An expanded table takes the whole Résumé area; Escape collapses it.
    const [expandedTable, setExpandedTable] = useState(null);
    const toggleExpand = (key) => {
        setExpandedTable(prev => (prev === key ? null : key));
        window.scrollTo({top: 0, behavior: "smooth"});
    };

    useEffect(() => {
        if (!expandedTable) return undefined;
        const handleKeyDown = (event) => {
            if (event.key === "Escape") setExpandedTable(null);
        };
        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [expandedTable]);

    useEffect(() => {
        if (active !== "resumer") setExpandedTable(null);
    }, [active]);

    // Popup states
    const [isPopupVisible, setPopupVisible] = useState(false);
    const [isPopupVisible2, setIsPopupVisible2] = useState(false);

    // Popup handlers
    const handlePopupToggle = () => setPopupVisible(!isPopupVisible);

    const handleExportClick = () => {
        setIsPopupVisible2(true);
        setPopupVisible(false);
    };

    const handleClosePopup = () => setIsPopupVisible2(false);

    // Sticky header shadow: on as soon as the zero-height sentinel above it
    // scrolls out of view (works with the Sidebar's inner scroll container).
    const stickySentinelRef = useRef(null);
    const [isScrolled, setIsScrolled] = useState(false);
    useEffect(() => {
        const sentinel = stickySentinelRef.current;
        if (!sentinel || typeof IntersectionObserver === "undefined") return undefined;
        const observer = new IntersectionObserver(([entry]) => setIsScrolled(!entry.isIntersecting));
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, []);

    // Compare accounts (points de vente) modal
    const [isCompareModalOpen, setIsCompareModalOpen] = useState(false);

    return (
        <div data-testid="activity-page" className={`${isDarkMode ? 'bg-gray-900 text-gray-100' : 'bg-white text-gray-900'}`}
             style={{fontSize: "12px"}}>
            <div ref={stickySentinelRef} aria-hidden="true"/>
            {/* Tabs + period stay pinned on every Activité tab */}
            <div className={`sticky top-0 z-30 border-b transition-shadow duration-200 ${
                isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'
            } ${isScrolled ? (isDarkMode ? 'shadow-lg shadow-black/40' : 'shadow-md') : ''}`}>
            <PageHeader
                tabs={tabs}
                active={active}
                setActive={setActive}
                handlePopupToggle={handlePopupToggle}
                isDarkMode={isDarkMode}
                isDropdownOpen={isDropdownOpen}
                setIsDropdownOpen={setIsDropdownOpen}
                selectedPeriodLabel={selectedPeriodLabel}
                setSelectedPeriodLabel={setSelectedPeriodLabel}
                setDateRange={setDateRange}
                onRefresh={handleRefresh}
                isRefreshing={isRefreshing}
                onCompareClick={() => setIsCompareModalOpen(true)}
                productFilterControl={active === "resumer" && (
                    <ProductFilterPicker
                        value={productFilter}
                        onChange={setProductFilter}
                        isDarkMode={isDarkMode}
                    />
                )}
                comparePeriodControl={active === "resumer" && (
                    <ComparePeriodSelector
                        compareMode={compareMode}
                        setCompareMode={setCompareMode}
                        comparisonRange={comparisonRange}
                        dateRange={dateRange}
                        isDarkMode={isDarkMode}
                    />
                )}
            />

            <div className="flex flex-col items-center w-full mx-auto gap-2 px-4 py-4">
                <DateRangePicker
                    dateRange={dateRange}
                    setDateRange={setDateRange}
                    isDarkMode={isDarkMode}
                />
                <TimeRangePicker
                    startTime={startTime}
                    setStartTime={setStartTime}
                    endTime={endTime}
                    setEndTime={setEndTime}
                    isDarkMode={isDarkMode}
                />
            </div>
            </div>

            <CompareAccountsModal
                isOpen={isCompareModalOpen}
                onClose={() => setIsCompareModalOpen(false)}
                isDarkMode={isDarkMode}
                initialDateRange={dateRange}
            />

            {active === "resumer" && productFilter.length > 0 && (
                <ProductStatsView
                    selection={productFilter}
                    onChange={setProductFilter}
                    dateRange={dateRange}
                    startTime={startTime}
                    endTime={endTime}
                    comparisonRange={comparisonRange}
                    refreshKey={refreshKey}
                    isDarkMode={isDarkMode}
                />
            )}

            {active === "resumer" && productFilter.length === 0 && (
                <>
                    {!expandedTable && (
                        <StatsCards
                            _dateRange={dateRange}
                            _startTime={startTime}
                            _endTime={endTime}
                            isMobile={true}
                            isDarkMode={isDarkMode}
                            refreshKey={refreshKey}
                            comparisonRange={comparisonRange}
                        />
                    )}
                    <main className="p-0">
                        {!expandedTable && (
                            <div className="flex flex-col md:flex-row justify-between w-full">
                                <div className="w-full md:w-3/4 min-h-[320px]">
                                <ChartSection
                                    _dateRange={dateRange}
                                    _startTime={startTime}
                                    _endTime={endTime}
                                    isDarkMode={isDarkMode}
                                    refreshKey={refreshKey}
                                    comparisonRange={comparisonRange}
                                />
                                </div>
                                <StatsCards
                                    _dateRange={dateRange}
                                    _startTime={startTime}
                                    _endTime={endTime}
                                    isMobile={false}
                                    isDarkMode={isDarkMode}
                                    refreshKey={refreshKey}
                                    comparisonRange={comparisonRange}
                                />
                            </div>
                        )}
                        <div className="flex flex-col md:flex-row">
                            {expandedTable !== "table2" && (
                                <ExpandableTable
                                    isExpanded={expandedTable === "table1"}
                                    toggleExpand={() => toggleExpand("table1")}
                                    activeTab={activeTab1}
                                    setActiveTab={setActiveTab1}
                                    tabOptions={tabOptions1}
                                    _dateRange={dateRange}
                                    _startTime={startTime}
                                    _endTime={endTime}
                                    isDarkMode={isDarkMode}
                                    refreshKey={refreshKey}
                                />
                            )}
                            {expandedTable !== "table1" && (
                                <ExpandableTable
                                    isExpanded={expandedTable === "table2"}
                                    toggleExpand={() => toggleExpand("table2")}
                                    activeTab={visibleTab2}
                                    setActiveTab={setActiveTab2}
                                    tabOptions={tabOptions2}
                                    _dateRange={dateRange}
                                    _startTime={startTime}
                                    _endTime={endTime}
                                    isDarkMode={isDarkMode}
                                    refreshKey={refreshKey}
                                />
                            )}
                        </div>

                        {/* Restaurant insights panel — tables + order types */}
                        {isRestaurant && !expandedTable && (
                            <div className={`border-t ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}>
                                {/* Tab bar */}
                                <div className={`flex border-b ${isDarkMode ? "border-gray-700 bg-gray-900" : "border-gray-200 bg-white"}`}>
                                    {[
                                        {key: "tables", label: t("activityPage.restaurant.tablesPerf")},
                                        {key: "modes",  label: t("activityPage.restaurant.orderModes")},
                                    ].map((tab) => (
                                        <button
                                            key={tab.key}
                                            onClick={() => setActiveRestaurantTab(tab.key)}
                                            className={`py-2.5 px-4 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                                                activeRestaurantTab === tab.key
                                                    ? "text-primary border-primary bg-primary/5"
                                                    : isDarkMode
                                                        ? "text-gray-400 border-transparent hover:text-gray-300 hover:border-gray-600"
                                                        : "text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300"
                                            }`}
                                        >
                                            {tab.label}
                                        </button>
                                    ))}
                                </div>

                                {/* Tab content */}
                                {activeRestaurantTab === "tables" && (
                                    <TableMetrics
                                        _dateRange={dateRange}
                                        _startTime={startTime}
                                        _endTime={endTime}
                                        isDarkMode={isDarkMode}
                                        refreshKey={refreshKey}
                                    />
                                )}
                                {activeRestaurantTab === "modes" && (
                                    <OrderTypeMetrics
                                        _dateRange={dateRange}
                                        _startTime={startTime}
                                        _endTime={endTime}
                                        isDarkMode={isDarkMode}
                                        refreshKey={refreshKey}
                                    />
                                )}
                            </div>
                        )}
                    </main>

                    <AnalyticsDashboard
                        dateRange={dateRange}
                        startTime={startTime}
                        endTime={endTime}
                        isDarkMode={isDarkMode}
                        refreshKey={refreshKey}
                        onRefresh={handleRefresh}
                        comparisonRange={comparisonRange}
                    />
                </>
            )}

            {active === "journaux" &&
                <JournauxTables
                    _dateRange={dateRange}
                    _startTime={startTime}
                    _endTime={endTime}
                    isDarkMode={isDarkMode}
                    refreshKey={refreshKey}
                />}

            {active === "ventes" &&
                <Ventes
                    _dateRange={dateRange}
                    _startTime={startTime}
                    _endTime={endTime}
                    isDarkMode={isDarkMode}
                    refreshKey={refreshKey}
                />}

            {isPopupVisible && (
                <ExportPopup
                    handlePopupToggle={handlePopupToggle}
                    handleExportClick={handleExportClick}
                    dateRange={dateRange}
                    startTime={startTime}
                    endTime={endTime}
                    isDarkMode={isDarkMode}
                />
            )}

            {isPopupVisible2 &&
                <ExportConfirmPopup
                    handleClosePopup={handleClosePopup}
                    isDarkMode={isDarkMode}
                />}

            {active === "employee" &&
                <Employees
                    _dateRange={dateRange}
                    _startTime={startTime}
                    _endTime={endTime}
                    isDarkMode={isDarkMode}
                    refreshKey={refreshKey}
                />}
        </div>
    );
};

export default ActivityPage;
